import { HybridServiceAccessor } from "@app/common/hybrid-service-accessor";
import { IPeripheral } from "@app/interfaces/IPeripheral";
import { ITagVehicleMapping } from "@app/interfaces/ITag";
import { IVehiclePeripheralMapping } from "@app/interfaces/IVehiclePeripheralMapping";
import { IVehicleQueryOptions } from "@app/interfaces/IVehicleQueryOptions";
import { CameraOnlineStatus } from "@app/main/components/videos/models/camera-online-status";
import { CameraOnlineHistoryService } from "@app/main/components/videos/services/camera-online-history.service";
import { AccountFeatures } from "@enums/account/AccountFeature";
import { UnitStatus } from "@enums/UnitStatus";
import { VehicleType } from "@enums/VehicleType.enum";
import { FoundationPeripheralService } from "@foundation/_services/peripheral.service.ajs";
import { ICombinedPreferences } from "@interfaces/ICombinedPreferences";
import { IKeyValuePair } from "@interfaces/IKeyValuePair";
import { IPlot, IPlotTemperatureRecord } from "@interfaces/IPlot";
import { IProbe } from "@interfaces/IProbe";
import { IVehicle } from "@interfaces/IVehicle";
import { HardwareSource } from "@interfaces/IVehicleInput";
import { IVehicleTrailerMapping } from "@interfaces/IVehicleTrailerMapping";
import { TranslationService } from "@services/translation.service";
import * as angular from "angular";
import * as moment from "moment";
import { SettingsName } from "../class/SettingsName";
import { IVorRecord } from "../main/components/resourceManagement/vor/vor.interfaces";
import { CacheService } from "./cache.Service";
import { ConstantsService } from "./constantsService";
import { EnumService } from "./enum.service";
import { PlotService } from "./plot.service";
import { SessionService } from "./session.service";
import { TagResourceType, TagsService } from "./tags.service";
import { VeriDate } from "./veriDate.service";
import { VorService } from "./vor.service";

export interface IVehicleFeatureSet {
    VehicleId: number;
    SupportedFeatures: AccountFeatures;
    LatestFeatureDateTime: { Item1: AccountFeatures, Item2: string /* Date */ }[];
}

export interface IGetVehicleOptions {
    includeTrailerMappings?: boolean;
    includeVOR?: boolean;
    includeProbes?: boolean;
    includeLatestPlots?: boolean;
    includeLatestTemp?: boolean;
    includePeripheralMappings?: boolean;
    includeTags?: boolean;
    includeAllVehicles?: boolean;

    invalidateCache?: boolean;
    dontCopy?: boolean;
}

export interface ISpeedingEvent {
    PlotId: number;
    VehicleId: number;
    DriverId: number;
    PtvRoadSegmentId: number;
    DateTime: string;
    PlotSpeedKph: number;
    SpeedLimitKph: number;
}

export class VehicleService {
    private readonly cameraOnlineHistoryService = 
        new HybridServiceAccessor<CameraOnlineHistoryService>("cameraOnlineHistoryService", this.$injector);

    userOptions: ICombinedPreferences;
    isDemoSite: boolean;

    private vehiclesCache = "vehiclesCache";
    private vehicleProbesCache = "vehicleProbesCache";

    static $inject = ["$http", "$q", "cacheService", "tagsService", "plotService", "sessionService", "vorService", "veriDate", "constantsService", "translationService", "foundationPeripheralService", "$injector"];

    constructor(
        private readonly $http: angular.IHttpService,
        private readonly $q: angular.IQService,
        private readonly cacheService: CacheService,
        private readonly tagsService: TagsService,
        private readonly plotService: PlotService,
        private readonly sessionService: SessionService,
        private readonly vorService: VorService,
        private readonly veriDate: VeriDate,
        private readonly constantsService: ConstantsService,
        private readonly translationService: TranslationService,
        private readonly foundationPeripheralService: FoundationPeripheralService,
        private readonly $injector: angular.auto.IInjectorService
    ) {
        this.isDemoSite = this.constantsService.getIsDemoSite();
        this.cacheService.createCache(this.vehiclesCache, { maxAge: 60 * 60 * 1000 });
        this.cacheService.createCache(this.vehicleProbesCache, { maxAge: 60 * 60 * 1000 });
    }

    vehicleSearchPredicate(vehicle: IVehicle, text: string): boolean {
        text = text.toLowerCase();
        return vehicle.ShortReg.toLowerCase().indexOf(text) >= 0 || vehicle.Reg.toLowerCase().indexOf(text) >= 0;
    }

    searchVehicles(vehicles: IVehicle[], text: string) {
        return vehicles.filter(v => this.vehicleSearchPredicate(v, text));
    }

    isNonDrivableVehicleType(vehicle: IVehicle): boolean {
        return vehicle.VehicleType == VehicleType.Trailer
            || vehicle.VehicleType == VehicleType.Plant
            || vehicle.VehicleType == VehicleType.Asset;
    }

    findVehicleByReg(vehicles: IVehicle[], regToFind: string) {
        var lowerCaseReg = regToFind.toLowerCase().trim();

        for (let i = 0; i < vehicles.length; i++) {
            const vehicle = vehicles[i];

            if (this.getSanitizedReg(vehicle) === this.sanitizeTextForUrl(lowerCaseReg)) {
                return vehicle;
            }
        }

        return null;
    }

    getSanitizedReg(vehicle: IVehicle, sanitizeDelimeter = "") {
        return this.sanitizeTextForUrl(vehicle.ShortReg);
    }

    // Todo: Shouldnt be here
    sanitizeTextForUrl(text: string, sanitizeDelimeter = "") {
        return text.replace(/\W/g, "") // \W is the equivalent of [^0-9a-zA-Z_]
            .replace(new RegExp(" ", "g"), sanitizeDelimeter).toLowerCase();
    }

    getVehicleFeatureSets(invalidateCache = false): angular.IPromise<IVehicleFeatureSet[]> {
        return this.$http({
            url: "/api/vehicles/featuresets",
            method: "GET",
            cache: true,
            params: { minsToLive: 5, invalidateCache: invalidateCache }
        }).then((result: angular.IHttpResponse<IVehicleFeatureSet[]>) => {

            return result.data;
        });
    }

    getActiveTrailerMappings(invalidateCache: boolean): angular.IPromise<IVehicleTrailerMapping[]> {

        return this
            .$http({
                url: "/api/trailers/mappings/active",
                method: "GET",
                cache: true,
                params: { minsToLive: 5, invalidateCache: invalidateCache }
            })
            .then((result: angular.IHttpResponse<IVehicleTrailerMapping[]>) => {

                //result.data.forEach(mapping => {
                //    if (/*mapping.VehicleId == 97263 ||*/ mapping.TrailerId == 96246) {
                //        console.log(mapping);
                //    }
                //});

                return result.data;
            });
    }

    getVehicleTrailerMappings(vehicleId: number, startTime: moment.Moment, endTime: moment.Moment, invalidateCache = false): angular.IPromise<IVehicleTrailerMapping[]> {
        return this
            .$http({
                url: `/api/trailers/mappings/${vehicleId}/`,
                method: "GET",
                cache: true,
                params: {
                    start: startTime.toISOString(),
                    end: endTime.toISOString(),
                    minsToLive: 5,
                    invalidateCache: invalidateCache
                }
            })
            .then((result: angular.IHttpResponse<IVehicleTrailerMapping[]>) => {
                return result.data;
            });
    }

    getVehicleTrailerMappingsWithVehicles(vehicleId: number, startTime: moment.Moment, endTime: moment.Moment, invalidateCache = false): angular.IPromise<IVehicleTrailerMapping[]> {
        var vehiclePromise = this.getVehicles({ includePeripheralMappings: true });
        var trailerAssociationPromise = this.getVehicleTrailerMappings(vehicleId, startTime, endTime, invalidateCache);

        return this.$q.all([vehiclePromise, trailerAssociationPromise]).then(([vehicles, trailerAssociations]) => {
            trailerAssociations.forEach(ta => {
                ta.vehicle = this.findVehicle(vehicles, ta.VehicleId);
                ta.trailer = this.findVehicle(vehicles, ta.TrailerId);
            });

            return trailerAssociations;
        });
    }

    // Todo - Dont call these if not required, currently happening on every call
    // trailer mappings
    // probes
    // vor?

    getVehicle(vehicleId: number, options?: IGetVehicleOptions): angular.IPromise<IVehicle> {
        return this.getVehicles(options)
            .then(vehicles => {
                return this.findVehicle(vehicles, vehicleId);
            });
    }

    getVehicleFromDatabase(vehicleId: number, options?: IGetVehicleOptions): angular.IPromise<IVehicle> {
        return this
            .$http({
                url: `/api/vehicles/${vehicleId}`,
                method: "GET"
            })
            .then((result: angular.IHttpResponse<IVehicle>) => {
                return result.data;
            });
    }

    getVehiclesBasic(queryOptions?: IVehicleQueryOptions): angular.IPromise<IVehicle[]> {
        return this.getVehiclesFromDatabase(queryOptions);
    }

    getVehicles(options?: IGetVehicleOptions): angular.IPromise<IVehicle[]> {
        this.getUserOptions();

        const vehiclesPromise = this.getOrRetreiveVehicles(options?.invalidateCache, options?.dontCopy)
            .then(vehicles => {
                return this.isDemoSite ? this.mapVehiclesRegsForDemo(vehicles) : vehicles;
            });

        const tagMappingPromise = options?.includeTags ? this.tagsService.getTagVehicleMappings({}, options?.invalidateCache) : this.$q.when<ITagVehicleMapping[]>([]);
        const trailerMappingPromise = options?.includeTrailerMappings ? this.getActiveTrailerMappings(options?.invalidateCache) : this.$q.when<IVehicleTrailerMapping[]>([]);
        const vorPromise = options?.includeVOR ? this.vorService.getCurrentRecords(options?.invalidateCache) : this.$q.when<IVorRecord[]>([]);
        const probePromise = options?.includeProbes ? this.getVehicleProbesForAccount() : this.$q.when<IProbe[]>([]);
        const tempPromise = options?.includeLatestTemp ? this.getLatestVehicleTemperatureRecords() : this.$q.when<IPlotTemperatureRecord[]>([]);
        const plotsPromise = options?.includeLatestPlots ? this.plotService.getLatestPlots() : this.$q.when<IPlot[]>([]);
        const peripheralPromise = options?.includePeripheralMappings ? this.foundationPeripheralService.getAllPeripherals() : this.$q.when<IPeripheral[]>([]);
        const peripheralMappingPromise = options?.includePeripheralMappings ? this.foundationPeripheralService.getAllPeripheralMappings() : this.$q.when<IVehiclePeripheralMapping[]>([]);
        const onlineStatusesPromise = options?.includePeripheralMappings ? this.cameraOnlineHistoryService.service.getCameraOnlineStatuses() as angular.IPromise<CameraOnlineStatus[]> : this.$q.when<CameraOnlineStatus[]>([]);

        return this.$q
            .all([
                tagMappingPromise,
                vehiclesPromise,
                vorPromise,
                trailerMappingPromise,
                probePromise,
                tempPromise,
                plotsPromise,
                peripheralPromise,
                peripheralMappingPromise,
                onlineStatusesPromise
            ]).then(([
                tagMappings,
                vehicles,
                vorRecords,
                trailerMappings,
                probes,
                tempRecords,
                plots,
                peripherals,
                peripheralMappings,
                onlineStatuses]) => {

                this.tagsService.mapTagMappingsToResources(tagMappings, vehicles, TagResourceType.Vehicle);

                if (options?.includePeripheralMappings) {

                    this.foundationPeripheralService.mapPeripheralsAndVehicles(vehicles, peripherals, peripheralMappings, onlineStatuses)
                }

                this.mapRecordsToVehicles(vehicles, plots, trailerMappings, vorRecords, probes, tempRecords);
                return vehicles;

            })

    }

    private mapVehiclesRegsForDemo(vehicles: IVehicle[]): IVehicle[] {
        return vehicles.map((vehicle, index) => {
            const reg = `V-${index + 1}`;

            return {
                ...vehicle,
                ShortReg: reg,
                Reg: reg
            }
        })
    }

    private mapRecordsToVehicles(
        vehicles: IVehicle[],
        plots: IPlot[],
        trailerMappings: IVehicleTrailerMapping[],
        vorRecords: IVorRecord[],
        probes: IProbe[], tempRecords: IPlotTemperatureRecord[],
    ) {
        this.setVehiclePlots(vehicles, plots);

        vehicles.forEach(vehicle => {
            this.mapTrailersToVehicle(vehicle, vehicles, trailerMappings);
            this.mapProbesToVehicle(vehicle, probes);
            this.mapVorToVehicle(vehicle, vorRecords);
            this.mapTempRecordsToVehicle(vehicle, tempRecords);
        });
    }

    private mapTempRecordsToVehicle(vehicle: IVehicle, tempRecords: IPlotTemperatureRecord[]) {
        vehicle.Temperatures = tempRecords.filter(r => r.VehicleId == vehicle.Id);

    }

    mapTrailersToVehicle(vehicle: IVehicle, vehicles: IVehicle[], trailerMappings: IVehicleTrailerMapping[]) {
        // Clear out any existing mappings
        if (vehicle.attachedTrailers) {
            vehicle.attachedTrailers.forEach(trailer => {
                trailer.tractor = null;
            });
        }

        vehicle.attachedTrailers = [];

        if (this.userOptions?.TrailerAssociations.Value) {
            var vehicleMappings = trailerMappings.filter(mapping => vehicle.Id == mapping.VehicleId);
            vehicleMappings.forEach(mapping => {
                //var vehicle = this.findVehicle(vehicles, mapping.VehicleId);
                var trailer = this.findVehicle(vehicles, mapping.TrailerId);

                if (vehicle && trailer) {
                    trailer.tractor = vehicle;
                    trailer.trailerMapping = mapping;

                    vehicle.attachedTrailers.push(trailer);

                    if (vehicle.attachedTrailers.length > 1) {
                        console.error("Two trailers attached to a single tractor", vehicle.attachedTrailers, mapping, trailerMappings);
                    }
                }
            });
        }
    }

    mapVorToVehicle(vehicle: IVehicle, vorRecords: IVorRecord[]) {
        vehicle.vorRecord = vorRecords.find(vor => vor.VehicleId == vehicle.Id);
    }

    updateVehicle(vehicle: IVehicle, invalidateCache = false): angular.IPromise<any> {
        return this.$http({
            url: `/api/vehicles/`,
            method: "POST",
            data: vehicle,
            params: { invalidateCache }
        }).then((result: angular.IHttpPromiseCallbackArg<any>) => {

            return this.tagsService.saveResourceTags(vehicle, TagResourceType.Vehicle).then(() => {

                this.updateOrInsertVehicleCache(vehicle);

                return result;
            });
        });

    }

    updateDepot(vehicleId: number, newDepotId: number): angular.IPromise<any> {
        return this.$http({
            url: `/api/vehicles/${vehicleId}/depot/${newDepotId}`,
            method: "PUT"
        });
    }

    updateOrInsertVehicleCache(vehicle: IVehicle): void {
        this.cacheService.addOrUpdateItem(this.vehiclesCache, vehicle, (v1, v2) => v1.Id == v2.Id);
    }

    setVehiclePlots(vehicles: IVehicle[], plots: IPlot[]): void {
        if (!plots || plots.length == 0) {
            return;
        }

        const plotMap = new Map(plots.map(plot => [plot.VehicleId, plot]));
        const twoDaysAgo = moment().add(-2, "day");

        vehicles.forEach(vehicle => {
            const plot = plotMap.get(vehicle.Id);

            if (plot) {
                vehicle.Plot = plot;
                vehicle.PlotId = plot.Id;
            }

            const vehicleHasPlotFromLastTwoDays = vehicle.Plot && vehicle.Plot.UtcMoment && vehicle.Plot.UtcMoment.isAfter(twoDaysAgo);

            vehicle.unitStatus = vehicleHasPlotFromLastTwoDays
                ? UnitStatus.Responding
                : UnitStatus.NoResponseWithin48Hours;
        });
    }

    updateVehicleProbe(probe: IProbe, invalidateCache = false): angular.IPromise<IProbe> {
        // Angular version in TemperatureProbeService
        return this
            .$http({
                url: "/api/temperature/probes",
                method: "POST",
                cache: invalidateCache,
                data: probe
            })
            .then((result: angular.IHttpResponse<IProbe>) => {
                this.cacheService.addOrUpdateItem(this.vehicleProbesCache, probe, (v1, v2) => v1.Id == v2.Id);

                return result.data;
            });
    }

    getProbes(vehicleId: number, invalidateCache = false): angular.IPromise<IProbe[]> {
        // Angular version in TemperatureProbeService
        return this
            .$http({
                url: `/api/temperature/probes/${vehicleId}`,
                method: "GET",
                cache: true,
                params: { minsToLive: 60, invalidateCache: invalidateCache }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IProbe[]>) => {
                return response.data;
            });
    }

    getVehicleProbes(vehicle: IVehicle, invalidateCache = false): angular.IPromise<IProbe[]> {
        return this.getProbes(vehicle.Id, invalidateCache)
            .then((probes: IProbe[]) => {
                return this.sanitiseVehicleProbes(vehicle, probes);
            });
    }

    sanitiseVehicleProbes(vehicle: IVehicle, probes: IProbe[]): IProbe[] {
        probes.forEach(probe => {
            this.cleanVehicleProbeName(vehicle.Reg, probe);
            this.cleanVehicleProbeName(vehicle.ShortReg, probe);
            probe.showProbe = true;
            probe.canChangeZone = probe.Source !== HardwareSource.Ibox && probe.Source !== HardwareSource.Carrier;
        });

        return probes;
    }

    getVehicleProbesForAccount(): angular.IPromise<IProbe[]> {
        // Angular version in TemperatureProbeService
        return this
            .$http({
                url: `/api/temperature/probes`,
                method: "GET",
                params: { minsToLive: 60 }
            })
            .then((response: angular.IHttpResponse<IProbe[]>) => {
                return response.data;
            });
    }

    cleanVehicleProbeName(vehicleReg: string, probe: IProbe) {
        // Deprecated - use TemperatureProbeService.cleanVehicleProbeName() instead
        try {
            var regEx = new RegExp(vehicleReg, "ig");
            var replaceMask = "";

            // Remove vehicle registration from probe name
            probe.cleanName = probe.Name.replace(regEx, replaceMask);

            // Remove all non-alphanumerics from start of strong (i.e. hypens)
            while (/[^a-zA-Z0-9]/.test(probe.cleanName[0]) && probe.cleanName) {
                probe.cleanName = probe.cleanName.slice(1).trim();
            }

            if (!probe.cleanName) {
                probe.cleanName = vehicleReg;
            }
        }
        catch (err) {
        }
    }

    getLatestVehicleTemperatureRecords(ignoreOldData = true): angular.IPromise<IPlotTemperatureRecord[]> {
        // Angular version in TemperatureRecordService
        return this
            .$http({
                url: `/api/temperature/latest`,
                method: "GET",
                params: { minsToLive: 1 }
            })
            .then((response: angular.IHttpResponse<IPlotTemperatureRecord[]>) => {

                if (ignoreOldData) {
                    var today = moment();
                    return response.data.filter(record => { return this.veriDate.createMoment(record.Timestamp).isSame(today, "d"); });
                }

                return response.data;
            });
    }

    getLatestVehicleTemperatureRecordsForVehicle(vehicleId: number): angular.IPromise<IPlotTemperatureRecord[]> {
        // Angular version in TemperatureRecordService
        return this
            .$http({
                url: `/api/temperature/${vehicleId}/latest`,
                method: "GET",
                params: { minsToLive: 1 }
            })
            .then((response: angular.IHttpResponse<IPlotTemperatureRecord[]>) => {
                return response.data;
            });
    }

    getVehicleTemperatureRecords(vehicleId: number, startDate: moment.Moment | Date, endDate: moment.Moment | Date, invalidateCache = false): angular.IPromise<IPlotTemperatureRecord[]> {
        // Angular version in TemperatureRecordService
        return this
            .$http({
                url: `/api/temperature/${vehicleId}`,
                method: "GET",
                params: { minsToLive: 60, startDate: startDate.toISOString(), endDate: endDate.toISOString(), invalidateCache: invalidateCache }
            })
            .then((response: angular.IHttpResponse<IPlotTemperatureRecord[]>) => {
                // Sort data first as it seems to be unordered
                response.data.sort((x, y) => {
                    return x.Id - y.Id;
                });

                return response.data;
            });
    }

    getVehicleTypes(): IKeyValuePair<VehicleType>[] {
        const vehicleTypes = EnumService.getItemsForSelect<VehicleType>(VehicleType);

        vehicleTypes.forEach(type => {
            type.displayName = this.translationService.translateEnum(VehicleType, "VehicleType", type.value as any);
        });

        return vehicleTypes;
    }

    updateOdometer(vehicleId: number, newOdometer: number, oldOdometer: number) {
        return this
            .$http({
                method: "POST",
                url: `/api/vehicles/odometer`,
                data: {
                    VehicleId: vehicleId,
                    NewOdometer: newOdometer,
                    OldOdometer: oldOdometer
                }
            })
            .then((result) => {
                return result.data;
            });
    }

    showVehicleWithNoUnit(vehicle: IVehicle): boolean {
        let addVehicle = false;

        if (vehicle.Plot !== null || this.userOptions.ShowVehiclesNoUnit.Value) {
            addVehicle = true;
        }

        return addVehicle;
    }

    showNonFunctioningUnit(vehicle: IVehicle): boolean {
        let addVehicle = false;

        if (this.userOptions.ShowVehiclesNotTracking.Value) {
            addVehicle = true;
        }

        if (!this.userOptions.ShowVehiclesNotTracking.Value && vehicle.unitStatus === UnitStatus.Responding) {
            addVehicle = true;
        }

        return addVehicle;
    }

    findVehicle(vehicles: IVehicle[], vehicleId: number): IVehicle {
        for (let i = 0; i < vehicles.length; i++) {
            const vehicle = vehicles[i];

            if (vehicle.Id === vehicleId) {
                return vehicle;
            }
        }

        return null;
    }

    findProbesForVehicle(vehicleId: number, probes: IProbe[]): IProbe[] {
        var vehicleProbes: IProbe[] = [];
        for (let i = 0; i < probes.length; i++) {
            const probe = probes[i];

            if (probe.VehicleId === vehicleId) {
                vehicleProbes.push(probe);
            }
        }

        return vehicleProbes;
    }

    findTempRecordsForPlot(tempRecords: IPlotTemperatureRecord[], plotId: number): IPlotTemperatureRecord[] {
        return tempRecords.filter(record => { return record.PlotId == plotId });
    }

    mapProbesToVehicles(vehicles: IVehicle[], probes: IProbe[]) {
        vehicles.forEach(vehicle => {
            this.mapProbesToVehicle(vehicle, probes);
        });
    }

    mapProbesToVehicle(vehicle: IVehicle, probes: IProbe[]) {
        vehicle.probes = this.findProbesForVehicle(vehicle.Id, probes);

        if (vehicle.probes) {
            vehicle.probes.forEach(probe => {
                this.cleanVehicleProbeName(vehicle.Reg, probe);
                //console.log(probe.Name, probe.cleanName);
            });
        }
    }

    vehicleHasUnitOrPeripheral(vehicle: IVehicle): boolean {
        return vehicle.HasUnit || this.vehicleHasPeripheral(vehicle);
    }

    vehicleHasPeripheral(vehicle: IVehicle): boolean {
        return vehicle.peripherals?.length > 0;
    }

    getRegPlateWithSpace(vehicle: IVehicle): string {
        if (vehicle.ShortReg.indexOf(" ") >= 0) {
            return vehicle.ShortReg;
        }

        if (vehicle.Reg.indexOf(" ") >= 0) {
            return vehicle.Reg;
        }

        // return in format A1 ABC
        if (vehicle.Reg.length === 5) {
            return `${vehicle.Reg.slice(0, 2)} ${vehicle.Reg.slice(2)}`
        }

        // return in format AB12 ABC
        if (vehicle.Reg.length === 7) {
            return `${vehicle.Reg.slice(0, 4)} ${vehicle.Reg.slice(4)}`
        }

        return vehicle.Reg;
    }

    regOrShortRegHasSpace(vehicle: IVehicle): boolean {
        return vehicle.ShortReg.indexOf(" ") >= 0 || vehicle.Reg.indexOf(" ") >= 0;
    }

    createRobustRegPlateSearchString(vehicle: IVehicle): string {
        if (!vehicle) {
            return;
        }

        let searchString = `${vehicle.ShortReg} ${vehicle.Reg}`;

        if (!this.regOrShortRegHasSpace(vehicle)) {
            searchString += this.getRegPlateWithSpace(vehicle);
        }

        return searchString;
    }

    getIconForVehicleType(vehicleType: VehicleType) {
        switch (vehicleType) {
            case VehicleType.HgvGreaterThan22T:
            case VehicleType.Hgv12Tto22T:
                return "fa-solid fa-duotone fa-truck-moving";

            case VehicleType.HgvLessThan12T:
            case VehicleType.Lcv:
                return "fa-solid fa-duotone fa-truck";

            case VehicleType.BusOrPSV:
                return "fa-solid fa-duotone fa-truck";

            case VehicleType.Trailer:
                return "fa-solid fa-duotone fa-trailer";

            default:
                return "fa-solid fa-duotone fa-car";
        }
    }

    // ------------------------------------------------

    private getOrRetreiveVehicles(invalidateCache: boolean = false, unsafe = false): angular.IPromise<IVehicle[]> {
        return this.cacheService.getOrRetrieveData(this.vehiclesCache, () => this.getVehiclesFromDatabase(), invalidateCache, unsafe);
    }

    private getVehiclesFromDatabase(queryOptions: IVehicleQueryOptions = {}): angular.IPromise<IVehicle[]> {
        this.getUserOptions();

        if (queryOptions.HasInstalledDevice === null || queryOptions.HasInstalledDevice === undefined) {
            queryOptions.HasInstalledDevice = !this.userOptions?.ShowVehiclesNoUnit.Value;
        }        

        return this
            .$http({
                url: "/api/vehicles",
                method: "GET",
                params: queryOptions
            })
            .then((result: angular.IHttpResponse<IVehicle[]>) => {
                this.getUserOptions();

                return result.data;
            });
    }



    private getUserOptions() {
        if (this.userOptions) {
            return {};
        }

        this.userOptions = this.sessionService.getItem<ICombinedPreferences>(SettingsName.UserOptions);
    }
}
